import { CollapseButton, SidebarStatusActionTypes } from "@moznsystems/mozn-ui";
import { motion } from "framer-motion";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatch } from "react-router-dom";
import {
	GROUPS,
	getNavigationItems,
	NavigationType,
} from "@/constants/navigation";
import { LocalizedNavLink } from "@/router/localized-link";
import { stripLeadingLocale } from "@/router/use-localized-href";
import { useSidebarStatusContext } from "@/store/sidebar-status-context";

const isUrlActive = (pathname: string, url: string): boolean => {
	const canonical = stripLeadingLocale(pathname);
	return canonical === url || canonical.startsWith(`${url}/`);
};

type SideBarProps = {
	setDrawerVisible: (status: boolean) => void;
};

const headerTabVariants = {
	open: {
		transition: { staggerChildren: 0.07, delayChildren: 0.05 },
	},
	closed: {
		transition: { staggerChildren: 0.05, staggerDirection: -1 },
	},
};

const headerSubTabVariants = {
	open: {
		y: 0,
		opacity: 1,
		transition: {
			y: { stiffness: 1000, velocity: -100 },
		},
	},
	closed: {
		y: 50,
		opacity: 0,
		transition: {
			y: { stiffness: 1000 },
		},
	},
};

export const SideBar = ({ setDrawerVisible }: SideBarProps) => {
	const location = useLocation();
	const [expandedItem, setExpandedItem] = useState<string | undefined>("/");
	const { sidebarStatus, handleSidebarStatus } = useSidebarStatusContext();
	const { t } = useTranslation("layout");

	let previousGroup = "";
	const toggleExpandItem = (url: string) => {
		setExpandedItem((prev) => (prev === url ? undefined : url));
	};

	return (
		<ul className="dashboard_menu">
			{getNavigationItems().map((i: NavigationType, index) => {
				const showGroup = i.group !== previousGroup;
				previousGroup = i.group;

				return (
					<Fragment key={index}>
						{showGroup && i.group !== GROUPS.MAIN && (
							<div className="side-bar__nav__group">
								<p className="side-bar__nav__group--title">{t(i.group)}</p>
							</div>
						)}
						<li
							className={
								"side-bar__nav__item" +
								(isUrlActive(location.pathname, i.url)
									? " side-bar__nav__item--active"
									: "")
							}
							key={`side-bar__nav__item-${index}`}
						>
							<div className="side-bar__nav__item--header">
								{i.subItems.length > 0 ? (
									<>
										<div
											className="side-bar__nav__item--main"
											key={i.url}
											onClick={() => {
												if (!i.subItems?.length) {
													setDrawerVisible(false);
												}
												toggleExpandItem(i.url);
											}}
										>
											<div>
												{i.icon && (
													<i.icon className="side-bar__nav__item__icon" />
												)}
												<span>{t(i.label)}</span>
											</div>
										</div>
										<div onClick={(event) => event.stopPropagation()}>
											<CollapseButton
												collapsed={expandedItem !== i.url}
												toggleCollapse={() => toggleExpandItem(i.url)}
												iconStyle
											/>
										</div>
									</>
								) : (
									<LocalizedNavLink
										key={i.url}
										onClick={() => {
											setDrawerVisible(false);
										}}
										className="side-bar__nav__item--main"
										to={i.url}
									>
										<div>
											{i.icon && (
												<i.icon className="side-bar__nav__item__icon" />
											)}
											<span>{t(i.label)}</span>
										</div>
									</LocalizedNavLink>
								)}
							</div>
							{i.subItems && i.subItems.length > 0 ? (
								<motion.ul
									variants={headerTabVariants}
									initial={false}
									animate={expandedItem === i.url ? "open" : "closed"}
									className={expandedItem === i.url ? "nav--expanded" : ""}
								>
									{i.subItems.map((subItem, index) => {
										let match = false;
										subItem?.routes?.forEach((routeURL) => {
											const isMatch = useMatch({
												path: routeURL,
												end: true,
											});
											if (isMatch) match = true;
										});

										return (
											<motion.li
												variants={headerSubTabVariants}
												key={index}
												className={
													"side-bar__nav__sub_item" +
													(match ? " side-bar__nav__sub_item--active" : "")
												}
											>
												<>
													<LocalizedNavLink
														onClick={() => {
															setDrawerVisible(false);
															if (
																sidebarStatus ===
																SidebarStatusActionTypes.TEMPORARILY_OPEN_SIDEBAR
															) {
																handleSidebarStatus(
																	SidebarStatusActionTypes.CLOSE_SIDEBAR,
																);
															}
														}}
														to={subItem.url}
													>
														<span>{t(subItem.label)}</span>
													</LocalizedNavLink>
												</>
											</motion.li>
										);
									})}
								</motion.ul>
							) : null}
						</li>
					</Fragment>
				);
			})}
		</ul>
	);
};
